// Teacher-invite accept. Standalone card (PUBLIC_CHILD_PATH). Reads ?token=,
// previews the school, lets the teacher set a password, then lands them in the
// teacher shell. R9.

import { useLocale, useT } from '@fajrarabi/i18n/react';
import { Alert, BrandLockup, Button, Field, Input, Spinner } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { LangSwitcher } from '~/components/LangSwitcher';
import { ApiError } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';
import { acceptTeacherInvite, previewTeacherInvite } from '~/lib/school';

export const Route = createFileRoute('/$locale/teacher-invite')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: TeacherInvitePage,
});

function TeacherInvitePage() {
  const t = useT();
  const { isRtl } = useLocale();
  const navigate = useNavigate();
  const { locale: rawLocale } = useParams({ from: '/$locale/teacher-invite' });
  const locale = asLocale(rawLocale);
  const { token } = useSearch({ from: '/$locale/teacher-invite' });
  const { refresh } = useAuth();

  const preview = useQuery({
    queryKey: ['invite', 'teacher', token],
    queryFn: () => previewTeacherInvite(token ?? ''),
    enabled: Boolean(token),
  });

  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setSubmitting(true);
    try {
      await acceptTeacherInvite(token, password);
      await refresh();
      await navigate({ to: '/$locale', params: { locale } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('app.common.error_generic'));
    } finally {
      setSubmitting(false);
    }
  }

  const invalid = !token || (preview.data && !preview.data.valid);

  return (
    <div className="min-h-dvh bg-cream-50">
      <header className="flex items-center justify-between px-6 py-5">
        <BrandLockup label={t('app.common.app_name')} />
        <LangSwitcher locale={locale} ariaLabel={t('app.common.lang_switch_label')} />
      </header>

      <main className="grid place-items-center px-6 pb-16 pt-6">
        <div className="w-full max-w-md">
          <div
            className="rounded-3xl bg-white p-8 shadow-[0_24px_60px_-30px_rgba(31,26,23,0.18)] ring-1 ring-burgundy-500/10"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            {preview.isLoading ? (
              <div className="grid place-items-center py-8">
                <Spinner />
              </div>
            ) : invalid ? (
              <Alert tone="danger">{t('app.school.invite_invalid')}</Alert>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-ink-900">
                  {t('app.school.invite_join_title', { school: preview.data?.schoolName ?? '' })}
                </h1>
                <p className="mt-1 text-sm text-ink-700">{preview.data?.email}</p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <Field
                    label={t('app.auth.password_label')}
                    hint={t('app.auth.password_hint_parent')}
                  >
                    {(id) => (
                      <Input
                        id={id}
                        type="password"
                        required
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    )}
                  </Field>

                  {error && <Alert tone="danger">{error}</Alert>}

                  <Button type="submit" loading={submitting} fullWidth>
                    {t('app.school.invite_accept')}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
